import { useQuery } from '@apollo/client';
import { captureMessage } from '@sentry/react';
import { EFilterType, TFilter, TFilterEvent } from 'Components/Filter/filter.types';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import usePartnerRequestActions from 'FeatureModules/Partner/Components/PartnerRequests/usePartnerRequestActions';
import {
  GET_QUESTIONNAIRE_OVERVIEW,
  GET_QUESTIONNAIRE_COMPANY_RESPONSES,
} from 'graphql/secure/queries/questionnaire.queries';
import {
  IQuestionnaireResponseConnection,
  IQuestionnaireResponsesFilter,
  IQuestionnaireResponsesOrderBy,
  IQuestionnaireResponseSummary,
} from 'graphql/secure/queries/types/questionnaire-query.types';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  EPartnerRequestStatus,
  ERequestType,
  IPartnerRequest,
  TPartnerRequestStatus,
} from 'FeatureModules/Partner/types/partner.types';
import { IQuestionnaire } from 'FeatureModules/Questionnaire/types/questionnaire.types';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

const useQuestionnaire = () => {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const { updatePartnerRequest } = usePartnerRequestActions();
  const { setErrorMessage } = useMessages();

  const [query, setQuery] = useQueryParams(
    {
      requestedToCompanyId: withDefault(StringParam, null),
    },
    { removeDefaultsFromUrl: true }
  );

  const { requestedToCompanyId } = query;

  const {
    data,
    loading,
    error,
    refetch: refetchQuestionnaire,
  } = useQuery<{
    questionnaire: IQuestionnaire;
    questionnaireCompanyResponses: { summary: IQuestionnaireResponseSummary };
  }>(GET_QUESTIONNAIRE_OVERVIEW, {
    variables: { id: questionnaireId },
    skip: !questionnaireId,
  });

  const {
    data: questionnaireResponsesData,
    loading: questionnaireResponsesLoading,
    error: questionnaireResponsesError,
  } = useQuery<
    {
      questionnaireCompanyResponses: IQuestionnaireResponseConnection;
    },
    {
      questionnaireId: string;
      filters: IQuestionnaireResponsesFilter;
      orderBy: IQuestionnaireResponsesOrderBy;
    }
  >(GET_QUESTIONNAIRE_COMPANY_RESPONSES, {
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionnaireId: questionnaireId!,
      filters: {
        requestStatus: [EPartnerRequestStatus.PROVIDED_COMPLETED],
        requestedToCompanyId: requestedToCompanyId || undefined,
      },
      orderBy: { orderByMethod: 'ASC', orderByField: 'riskLevel' },
    },
    skip: !questionnaireId,
  });

  const questionnaire = data?.questionnaire;
  const pendingQuestionnairesRequests = questionnaire?.requestsSent.filter(
    request =>
      request.requestStatus === EPartnerRequestStatus.REQUESTED ||
      request.requestStatus === EPartnerRequestStatus.PROVIDED
  );

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.PARTNERS,
        value: requestedToCompanyId,
      },
    ],
    [requestedToCompanyId]
  );

  const questionnaireResponses = questionnaireResponsesData?.questionnaireCompanyResponses
    ? removeGraphConnections(questionnaireResponsesData.questionnaireCompanyResponses)
    : [];
  const questionnaireResponseSummary = data?.questionnaireCompanyResponses.summary;
  const hasCompletedResponses =
    questionnaireResponseSummary &&
    questionnaireResponseSummary.totalAnswers > questionnaireResponseSummary.pendingAnswers;

  const handleUpdateRequest = (requestId: string, requestStatus: TPartnerRequestStatus) => {
    updatePartnerRequest({
      variables: {
        id: requestId as string,
        input: {
          requestStatus: requestStatus,
        },
      },
      onError: () =>
        setErrorMessage('Something went wrong finishing the request. Please try again later.'),
    });
  };

  const handleEditQuestionnaire = () => {
    if (!questionnaire) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      console.error('No questionnaire found for editing');
      captureMessage('No questionnaire found for editing', { level: 'error' });
      return;
    }
    openDialog({
      type: 'ADD_EDIT_QUESTIONNAIRE',
      props: { questionnaire },
    });
  };

  const handlePreviewQuestions = () => {
    if (!questionnaireId) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      console.error('No questionnaire ID found for editing');
      captureMessage('No questionnaire ID found for previewing questions', { level: 'error' });
      return;
    }
    openDialog({
      type: 'PREVIEW_QUESTIONNAIRE',
      props: { questionnaireId },
    });
  };

  const handleViewResponse = (questionnaireResponseId: string) => {
    navigate(`/questionnaires/${questionnaireId}/responses/${questionnaireResponseId}`);
  };

  const handleCreateQuestionnaireRequest = () => {
    openDialog({
      type: 'PARTNER_REQUEST',
      props: {
        requestType: ERequestType.QUESTIONNAIRE,
        questionnaireId,
        onRequestCreated: () => {
          refetchQuestionnaire();
        },
      },
    });
  };

  const handleEditRequest = (request: IPartnerRequest) => {
    openDialog({
      type: 'PARTNER_REQUEST',
      props: {
        initialRequest: request,
        requestedToCompany: request.requestedToCompany,
        onUpdateCache: () => {
          refetchQuestionnaire();
        },
      },
    });
  };

  const handleAcceptRequest = (request: IPartnerRequest) => {
    openDialog({
      type: 'REVIEW_QUESTIONNAIRE_REQUEST',
      props: { request },
    });
  };

  const handleDeleteRequest = (request: IPartnerRequest) => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete request',
        itemTitle: request.requestTitle,
        submitText: 'Delete request',
        text: `Are you sure you want to delete this request?`,
        onSubmit: () => handleUpdateRequest(request.id, 'DELETED'),
        onCancel: () => undefined,
      },
    });
  };

  const handleFilterChange = (event: TFilterEvent) => {
    if (event.type === EFilterType.PARTNERS) {
      setQuery(prev => ({
        ...prev,
        requestedToCompanyId: (event.value as string) || undefined,
      }));
    }
  };

  const handleFilterReset = () => {
    setQuery(prev => ({
      requestedToCompanyId: undefined,
    }));
  };

  return {
    filters,
    questionnaire,
    loading: loading,
    questionnaireResponsesLoading,
    error: error || questionnaireResponsesError,
    pendingQuestionnairesRequests,
    hasCompletedResponses,
    questionnaireResponses,
    questionnaireResponseSummary,
    handleEditQuestionnaire,
    handlePreviewQuestions,
    handleViewResponse,
    handleCreateQuestionnaireRequest,
    handleEditRequest,
    handleDeleteRequest,
    handleFilterChange,
    handleAcceptRequest,
    handleFilterReset,
  };
};

export default useQuestionnaire;
